import React, { useMemo } from 'react';
import { ReviewQuestionWrapper } from '../shared/ReviewQuestionWrapper';
import { FillInTheBlankQuestionText, FillInTheBlankSentence } from '../shared/FillInTheBlankSections';
import { useFillInTheBlankData, parseSentenceWithBlanks, getChoiceTextByIdx, useFillInTheBlankUserAnswers } from '../../../../../hooks/useFillInTheBlankData';

const FillInTheBlankReview = ({ question, userAnswer = [], correctAnswer = [], isCorrect, accuracy, feedback = '', explanation = '', recap = '', annotations = [], reportProps = null }) => {
  // Transform backend data using custom hook
  const { blanks: initialBlanks, text, choicesMap } = useFillInTheBlankData(question);

  // Update blanks with correctAnswer from prop if available
  const blanks = useMemo(() => {
    if (!initialBlanks || initialBlanks.length === 0) return initialBlanks;
    if (!correctAnswer || correctAnswer.length === 0) return initialBlanks;
    
    // correctAnswer is array of indices in order of blanks
    return initialBlanks.map((blank, index) => {
      const correctIdx = Array.isArray(correctAnswer) ? correctAnswer[index] : null;
      const correctAnswerText = correctIdx !== null && correctIdx !== undefined && choicesMap[correctIdx]
        ? choicesMap[correctIdx]
        : blank.correctAnswer || '';
      
      return {
        ...blank,
        correctIdx: correctIdx !== null && correctIdx !== undefined ? correctIdx : blank.correctIdx,
        correctAnswer: correctAnswerText,
      };
    });
  }, [initialBlanks, correctAnswer, choicesMap]);

  // Parse sentence with blanks
  const parts = useMemo(() => {
    if (!blanks || blanks.length === 0) {
      return null;
    }
    return parseSentenceWithBlanks(text, blanks);
  }, [text, blanks]);

  // Helper function to get choice text by idx
  const getChoiceTextByIdxFn = useMemo(() => {
    return (idx) => getChoiceTextByIdx(question.choices, choicesMap, idx);
  }, [question.choices, choicesMap]);

  // Parse userAnswer into map by blankId
  const userAnswersByBlankId = useFillInTheBlankUserAnswers(userAnswer, blanks, choicesMap, getChoiceTextByIdxFn);

  return (
    <ReviewQuestionWrapper
      questionType="Fill in the Blank"
      question={question}
      explanation={explanation}
      recap={recap}
      annotations={annotations}
      reportProps={reportProps}
    >
      <FillInTheBlankQuestionText parts={parts} fallbackText={text || question.text || question['text-soal'] || ''} />

      {/* Correct Answer Section */}
      {parts && (
        <div className="mb-3">
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">Correct Answer:</h4>
          <FillInTheBlankSentence parts={parts} showAnswers={false} />
        </div>
      )}

      {/* Your Answer Section */}
      {parts && (
        <div className={feedback ? "mb-3" : "mb-6"}>
          <h4 className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">Your Answer:</h4>
          <FillInTheBlankSentence parts={parts} showAnswers={true} userAnswersByBlankId={userAnswersByBlankId} />
        </div>
      )}

      {/* Feedback per soal - di bawah Your Answer */}
      {feedback && (
        <div className="mb-6 -mt-1">
          <div className="p-5 rounded-lg border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-500/10">
            <h4 className="text-sm font-bold uppercase tracking-wide text-orange-600 dark:text-amber-400 mb-2">💡 Feedback:</h4>
            <div className="text-[15px] leading-[1.7] text-gray-800 dark:text-slate-200 [&_p]:m-0">
              {typeof feedback === 'string' ? (
                <p>{feedback}</p>
              ) : Array.isArray(feedback) ? (
                <div>
                  {feedback.map((fb, idx) => (
                    <p key={idx} className={idx < feedback.length - 1 ? 'mb-2' : ''}>{fb}</p>
                  ))}
                </div>
              ) : typeof feedback === 'object' && feedback !== null ? (
                <div>
                  {Object.entries(feedback).map(([key, value], idx) => (
                    <div key={idx} className="mb-2 last:mb-0">
                      <strong>Blank {parseInt(key) + 1}:</strong> {value}
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </ReviewQuestionWrapper>
  );
};

export default FillInTheBlankReview;

